import { useEffect, useMemo, useRef, useState } from 'react';
import type { AlgebraOperation, Equation } from '../../shared/algebra.ts';
import './operation-demonstration.css';
import { deriveOperationDemonstration, formatOperationExpression } from './operationDemonstrationModel.ts';

export { deriveOperationDemonstration } from './operationDemonstrationModel.ts';

export type OperationDemonstrationProps = {
  before: Equation;
  operation: AlgebraOperation;
  after: Equation;
  reduced: boolean;
  /** 0–1 progress from actual narration media; disables the local timeline. */
  mediaProgress?: number;
};

function ExpressionWithOperation({ terms, operationText, wraps, cancelled }: {
  terms: { variable: string; constant: string };
  operationText: string;
  wraps: boolean;
  cancelled: boolean;
}) {
  const hasConstant = terms.constant !== '0';
  const magnitude = terms.constant.startsWith('-') ? terms.constant.slice(1) : terms.constant;
  const join = terms.variable && hasConstant ? (terms.constant.startsWith('-') ? ' − ' : ' + ') : '';
  const constantDisplay = terms.variable
    ? magnitude
    : terms.constant.startsWith('-')
      ? `−${magnitude}`
      : magnitude;
  const expression = <>{terms.variable && <span>{terms.variable}</span>}{hasConstant && <>{join}<span className={cancelled ? 'operation-cancelled-term' : undefined}>{constantDisplay}</span></>}{!terms.variable && !hasConstant && <span>0</span>}</>;
  return wraps ? <span className="operation-whole-expression">({expression}) {operationText}</span> : <span>{expression} <span className={cancelled ? 'operation-cancelled-term' : undefined}>{operationText}</span></span>;
}

export function OperationDemonstration({
  before,
  operation,
  after,
  reduced,
  mediaProgress,
}: OperationDemonstrationProps) {
  const model = useMemo(
    () => deriveOperationDemonstration(before, operation, after),
    [before, operation, after],
  );
  const [progress, setProgress] = useState(reduced ? 1 : 0);
  const [paused, setPaused] = useState(false);
  const startedAt = useRef<number | undefined>(undefined);
  const pausedAt = useRef<number | undefined>(undefined);
  const frame = useRef<number | undefined>(undefined);
  const hasExternalProgress = Number.isFinite(mediaProgress);
  const displayedProgress = hasExternalProgress
    ? Math.max(0, Math.min(1, mediaProgress ?? 0))
    : progress;

  useEffect(() => {
    if (frame.current !== undefined) cancelAnimationFrame(frame.current);
    startedAt.current = undefined;
    pausedAt.current = undefined;
    setPaused(false);
    setProgress(reduced ? 1 : 0);
  }, [before, operation, after, reduced]);

  useEffect(() => {
    if (reduced || hasExternalProgress || paused || !model.valid || progress >= 1) return;
    const tick = (now: number) => {
      startedAt.current ??= now - progress * 2400;
      const next = Math.min(1, (now - startedAt.current) / 2400);
      setProgress(next);
      if (next < 1) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => {
      if (frame.current !== undefined) cancelAnimationFrame(frame.current);
      frame.current = undefined;
    };
  }, [hasExternalProgress, model.valid, paused, progress, reduced]);

  if (!model.valid)
    return (
      <section className="operation-demonstration operation-demonstration-invalid" aria-live="polite">
        <strong>That operation cannot be demonstrated.</strong>
        <span>The recorded equation change did not match one valid operation on both sides.</span>
      </section>
    );

  const showOperation = reduced || displayedProgress >= 0.18;
  const showCancellation = reduced || displayedProgress >= 0.5;
  const showAfter = reduced || displayedProgress >= 0.8;
  const label = model.wrapsExpressions
    ? `${model.operationText} every term in both entire expressions`
    : `${model.operationText} both entire expressions`;
  const appliedMathLabel = `${formatOperationExpression(model.left, model.operationText, model.wrapsExpressions)} equals ${formatOperationExpression(model.right, model.operationText, model.wrapsExpressions)}`;

  return (
    <section className="operation-demonstration" aria-label={`Operation demonstration: ${label}`}>
      <div className="operation-demonstration-heading">
        <span>Apply the same move to both sides</span>
        {!reduced && !hasExternalProgress && (
          <button
            type="button"
            className="operation-demonstration-control"
            onClick={() => {
              if (progress >= 1) {
                startedAt.current = undefined;
                pausedAt.current = undefined;
                setProgress(0);
                setPaused(false);
              } else if (paused) {
                if (startedAt.current !== undefined && pausedAt.current !== undefined)
                  startedAt.current += performance.now() - pausedAt.current;
                pausedAt.current = undefined;
                setPaused(false);
              } else {
                pausedAt.current = performance.now();
                setPaused(true);
              }
            }}
          >
            {progress >= 1 ? 'Replay' : paused ? 'Play' : 'Pause'}
          </button>
        )}
      </div>
      <div className={`operation-equation operation-before ${showOperation ? 'is-visible' : ''}`}>
        <span>{model.before}</span>
      </div>
      <div className={`operation-both ${showOperation ? 'is-visible' : ''}`} aria-label={appliedMathLabel}>
        <ExpressionWithOperation terms={model.left} operationText={model.operationText} wraps={model.wrapsExpressions} cancelled={showCancellation && model.leftCancellation} />
        <span className="operation-equals">=</span>
        <ExpressionWithOperation terms={model.right} operationText={model.operationText} wraps={model.wrapsExpressions} cancelled={showCancellation && model.rightCancellation} />
      </div>
      <div className={`operation-cancellation ${showCancellation ? 'is-visible' : ''}`} aria-live="polite">
        {model.leftCancellation || model.rightCancellation ? (
          <span>Only the matching inverse pair is removed; the equality stays balanced.</span>
        ) : (
          <span>{model.wrapsExpressions ? 'Scale every term; no terms cross the equals sign.' : 'No inverse constants cancel in this move.'}</span>
        )}
      </div>
      <div className={`operation-equation operation-after ${showAfter ? 'is-visible' : ''}`}>
        <span>{model.after}</span>
      </div>
    </section>
  );
}
